// Reusable masked-with-eye-icon field for PII (HKID, bank account, etc.).
//
// Defaults to hidden — admin clicks the eye icon to reveal in plain. The
// underlying value is held in component state on the client, so this is a
// visual / shoulder-surfing defense, not an auth boundary. Use alongside
// API-layer access control (admin role + per-permission gate) so the
// ciphertext never reaches a non-admin in the first place.
//
// Two modes:
//   - mode="display"  → read-only chip-row with the masked or revealed
//                       string. Used for fields surfaced from elsewhere
//                       (e.g. latest withdrawal bank info).
//   - mode="edit"     → wraps a TextField with a trailing eye toggle. The
//                       text is masked (type=password) until revealed.

import { useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";

type MaskFn = (raw: string) => string;

const fullMask: MaskFn = (raw) => "•".repeat(Math.min(raw.length, 12));

const last4Mask: MaskFn = (raw) => {
  const v = raw.replace(/\s+/g, "");
  if (v.length <= 4) return fullMask(v);
  return `${"•".repeat(Math.min(v.length - 4, 8))}${v.slice(-4)}`;
};

const yearOnlyMask: MaskFn = (raw) => {
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(raw);
  return m ? `${m[1]}-••-••` : fullMask(raw);
};

export type SensitiveMaskMode = "full" | "last4" | "year-only";

const maskFor = (mode: SensitiveMaskMode): MaskFn => {
  if (mode === "last4") return last4Mask;
  if (mode === "year-only") return yearOnlyMask;
  return fullMask;
};

// Display variant — no input, just a labelled value with an eye toggle.
export function SensitiveDisplay({
  label,
  value,
  mask = "full",
  empty = "Not on file",
}: {
  label: string;
  value: string | null | undefined;
  mask?: SensitiveMaskMode;
  empty?: string;
}) {
  const [shown, setShown] = useState(false);
  const hasValue = Boolean(value && value.length > 0);
  const masker = maskFor(mask);
  return (
    <Box>
      <Typography
        sx={{
          fontSize: tokens.font.sizes.bodySm,
          color: tokens.colors.admin.textMuted,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
        <Typography
          sx={{
            fontSize: tokens.font.sizes.body,
            color: hasValue ? tokens.colors.admin.textHeading : tokens.colors.admin.textMuted,
            fontStyle: hasValue ? "normal" : "italic",
            fontFamily: hasValue && !shown ? "monospace" : undefined,
          }}
        >
          {hasValue ? (shown ? value : masker(value!)) : empty}
        </Typography>
        {hasValue && (
          <Tooltip title={shown ? "Hide" : "Show"}>
            <IconButton
              size="small"
              onClick={() => setShown((s) => !s)}
              sx={{ p: 0.25 }}
              aria-label={shown ? `Hide ${label}` : `Show ${label}`}
            >
              {shown ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// Edit variant — TextField masked as `password` until revealed.
export function SensitiveTextField({
  label,
  value,
  onChange,
  helperText,
  placeholder,
  maxLength,
  required,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  helperText?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}) {
  const [shown, setShown] = useState(false);
  return (
    <TextField
      variant="standard"
      fullWidth
      required={required}
      label={label}
      type={shown ? "text" : "password"}
      placeholder={placeholder}
      helperText={helperText}
      inputProps={maxLength ? { maxLength } : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={shown ? "Hide" : "Show"}>
              <IconButton
                size="small"
                onClick={() => setShown((s) => !s)}
                edge="end"
                aria-label={shown ? `Hide ${label}` : `Show ${label}`}
              >
                {shown ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}
